import React, { useRef, useEffect } from 'react';
import useResizeAware from 'react-resize-aware';
import { getResolution } from '../Responsive/responsiveIdentifier';

/**
 * Um higher order component (HoC) que escuta mudanças
 * de resize num dado componente.
 *
 * Sempre que o tamanho (width x height) do componente muda,
 * é chamado, de forma procedural, na referencia para o componente
 * passado, a função "onResize", passando as seguintes propriedades
 * {newWidth, newHeight, resolution}.
 *
 * Esta abordagem foi utilizada pois, a priori, este HoC foi projetado
 * para atender a necessidade de atualizar o gráfico do apache echarts sem causar
 * uma ré-renderização no componente. O próprio echarts possui métodos como resize e
 * setOption para manipular o estado do gráfico. Uma ré-renderização iria fazer
 * com que a instancia do chart fosse recriada e esta criação é custosa.
 *
 * Desta forma, para um componente utilizar este HoC é necessário
 * implementar a função "onResize".
 *
 * @param {*} Component
 */
const sizeAware = (Component) => {
  const Wrapper = (props) => {
    const [resizeListener, sizes] = useResizeAware();
    const chartRef = useRef(null);

    function setWrappedComponentRef(instance) {
      chartRef.current = instance;
    }

    useEffect(() => {
      if (chartRef.current) {
        const resolution = getResolution(sizes.width);
        if (chartRef.current.onResize) {
          chartRef.current.onResize({
            newWidth: sizes.width,
            newHeight: sizes.height,
            resolution,
          });
        }
      }
    }, [sizes.width, sizes.height]);

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {resizeListener}
        <Component
          {...props}
          ref={
            (wrappedCompRef) => setWrappedComponentRef(wrappedCompRef)
          }
        />
      </div>
    );
  };

  return Wrapper;
};

export default sizeAware;
